import { useCallback, useEffect, useReducer, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { iso31662 } from 'iso-3166';
import graphql from 'services/querys';
import { deletePartyEmail, updateParty } from 'graphql/parties/mutations';
import { getBlockchainData } from 'graphql/wire-transfers/queries';
import { deletePartyPayment } from 'graphql/wire-transfers/mutations';
import { getRoles } from 'graphql/users/queries';
import { useSubscribeToSocketEvent } from 'hooks/useSubscribeToSocketEvent';
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';
import { useGetListings } from 'hooks/useGetListings';
import { useDeleteUser } from 'views/contacts/hooks/useDeleteUser';
import { InfoDeliveryType } from 'components/users/user-wire-transfers/types';
import { NewUserType } from 'v2-types/user';
import { gqlRequestPasswordReset } from './graphql/mutations';
import { formType } from './services';
import { useUserInformationContext } from 'contexts/userContext';
import { updateAICounters } from 'graphql/assistant/mutations';
import { infoUpdateType } from 'types/notifications';
import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';
import useAlert from 'utils/alert';

const typesOfnotifications = [
  "createPhone",
  "createEmail",
  "createAddress",
  "createLicense",
  "updateParty"
];

type DeleteModalState = {
  mode: 'delete' | null;
  data: { func: (...args: any[]) => void; params: Record<string, any> } | null;
  message: string;
  title: string;
};

const deleteModalReducer = (_state: DeleteModalState, action: any): DeleteModalState => {
  if (action.type === 'delete') {
    return {
      mode: 'delete',
      data: action.data,
      message: action.message,
      title: action.title,
    };
  }
  return { mode: null, data: null, message: '', title: '' };
};

const initialDeleteModalState: DeleteModalState = {
  mode: null,
  data: null,
  message: '',
  title: '',
};

export type ModalKey =
  | 'address'
  | 'email'
  | 'license'
  | 'phone'
  | 'account'
  | 'relations'
  | 'access'
  | 'recovery'
  | 'permissions'
  | 'ai-usage'
  | null;

const splitMemberKinds = (members: NewUserType['members']) =>
  members.reduce((prev, curr) => {
    const kindsSplit = curr.kind.split(',');
    if (kindsSplit.length > 1) {
      return [...prev, ...kindsSplit.map((k) => ({ ...curr, kind: k }))];
    }
    return [...prev, curr];
  }, [] as any);

export const useContactFormEdit = (
  contact: NewUserType,
  refreshContacts: () => void,
  onNameChange?: (name: string) => void
) => {
  const { t } = useTranslation();
  const queryCache = useQueryClient();
  const { currentUser } = useUserInformationContext();
  const [modals, setModals] = useState<ModalKey>(null);
  const { llmServiceRef } = useLLMProxyServiceContext();
  const [deleteModalState, dispatchDeleteModal] = useReducer(
    deleteModalReducer,
    initialDeleteModalState
  );
  const showAlert = useAlert();
  const model_agent = llmServiceRef.current?.getModelInference();
  // Form setup
  const methods = useForm<formType>({
    defaultValues: {
      ...contact,
      members: splitMemberKinds(contact.members),
    },
  });

  useEffect(() => {
    if (contact) {
      methods.reset({
        ...contact,
        members: splitMemberKinds(contact.members),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  const watchedName = methods.watch('name');

  useEffect(() => {
    if (onNameChange && watchedName) {
      onNameChange(watchedName);
    }
  }, [watchedName, onNameChange]);

  // Listings & roles queries
  const { data: kindList } = useGetListings({ code: 'allowed_party_kinds' });
  const { data: existingMutations } = useGetListings({ code: 'existing_mutations' });
  const { data: existingQuerys } = useGetListings({ code: 'existing_queries' });
  const { data: existingRoles } = useQuery(['existing-roles'], getRoles, {
    enabled: !!contact,
  });

  // States list
  const statesByCountry = [{ code: 'ALL', name: 'National License', parent: '' }].concat(
    iso31662
      .filter((state) => state.parent === 'US')
      .map((state) => ({ ...state, code: state.code.replace('US-', '') }))
  );

  // Mutations
  const { mutate: deleteAddress, ...responseDeleteAddress } = useMutation(graphql.deleteAddress, {
    onSuccess: (data) => {
      responseDeleteAddress.reset();
      methods.setValue('addresses', data.deleteAddress.addresses);
      dispatchDeleteModal({ type: null });
    },
  });

  const { mutate: deleteLicense, ...responseDeleteLicense } = useMutation(graphql.deleteLicense, {
    onSuccess: (response) => {
      if (response.deleteLicense) {
        responseDeleteLicense.reset();
        methods.setValue('licenses', response.deleteLicense.licenses);
        dispatchDeleteModal({ type: null });
      }
    },
  });

  const { mutate: deleteEmail, ...responseDeleteEmail } = useMutation(deletePartyEmail, {
    onSuccess: (response) => {
      if (response.deleteEmail) {
        responseDeleteEmail.reset();
        methods.setValue('emails', response.deleteEmail.emails);
        dispatchDeleteModal({ type: null });
      }
    },
  });

  const { mutate: deletePhone, ...responseDeletePhone } = useMutation(graphql.deletePhone, {
    onSuccess: (data) => {
      responseDeletePhone.reset();
      methods.setValue('phones', data.deletePhone.phones);
      dispatchDeleteModal({ type: null });
    },
  });

  const { mutate: deletePayment, isLoading: isDeleteWireTransferLoading } = useMutation(
    deletePartyPayment,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['contacts-wire-transfer', contact._id]);
        dispatchDeleteModal({ type: null });
      },
    }
  );

  const { mutate: deletePartyAssociation, ...responseDeletePartyAssociation } = useMutation(
    graphql.deletePartyAssociation,
    {
      onSuccess: () => {
        responseDeletePartyAssociation.reset();
        dispatchDeleteModal({ type: null });
        refreshContacts();
      },
    }
  );

  const { mutate: updatePartyMutation, ...responseUpdateParty } = useMutation(updateParty, {
    onSuccess: (response) => {
      if (response) {
        responseUpdateParty.reset();
        methods.setValue('name', response.updateParty.name);
        refreshContacts();
      }
    },
    onError: () => {
      responseUpdateParty.reset();
    },
  });

  const { mutate: deleteUser, ...responseDeleteUser } = useDeleteUser({
    onSuccess: () => {
      responseDeleteUser.reset();
      dispatchDeleteModal({ type: null });
      refreshContacts();
    },
    onError: () => {
      responseDeleteUser.reset();
    },
  });

  const { mutate: requestPasswordReset, isLoading: isRequestPasswordResetLoading } = useMutation(gqlRequestPasswordReset, {
    onSuccess: () => {
      setModals(null);
    },
  });

  const { mutate: resetAICounters, isLoading: isResettingAICounters } = useMutation(updateAICounters, {
    onSuccess: () => {
      queryCache.invalidateQueries(['aiUssage-inference', currentUser?._id]);
      showAlert(t('contact:reset-ai-counters-success'), 'success');
    },
  });

  // Wire transfer queries & sockets
  useQuery(
    ['contacts-wire-transfer', contact._id],
    () => getBlockchainData({ dataId: contact._id }),
    {
      refetchOnWindowFocus: false,
      onSuccess: () => {},
    }
  );

  useSubscribeToSocketEvent({
    eventKey: 'info-delivery',
    callback: (data: InfoDeliveryType) => {
      if (data?.document) {
        methods.setValue('paymentAccounts', data.document.wire || []);
      }
    },
  });

  useInfoUpdateEvent((data: infoUpdateType) => {
    if (['workerAccountRemoval', 'workerAccountAddition'].includes(data.kind)) {
      queryCache.invalidateQueries(['contacts-wire-transfer', contact._id]);
    }
  });

  // Field arrays
  const { control } = methods;
  const addresses = useFieldArray({ control, name: 'addresses' });
  const phones = useFieldArray({ control, name: 'phones' });
  const emails = useFieldArray({ control, name: 'emails' });
  const licenses = useFieldArray({ control, name: 'licenses' });
  const payments = useFieldArray({ control, name: 'paymentAccounts' });
  const parents = useFieldArray({ control, name: 'parties' });
  const subordinates = useFieldArray({ control, name: 'members' });
  const permissions = useFieldArray({ control, name: 'permissions' });

  const getOptions = useCallback(
    (type: string) => {
      switch (type) {
        case 'role':
          return existingRoles?.getRoles || [];
        case 'mutation':
          return existingMutations?.getListing.entries || [];
        case 'query':
          return existingQuerys?.getListing.entries || [];
        default:
          return [];
      }
    },
    [existingMutations, existingQuerys, existingRoles]
  );
  useInfoUpdateEvent((data: infoUpdateType) => {
    if (typesOfnotifications.includes(data.kind) && currentUser?.emails.some(({ address }) => address === data.email)) {
      refreshContacts();
    }
    if (data.kind === "createPartyPayment" && currentUser?.emails.some(({ address }) => address === data.email)) {
      queryCache.invalidateQueries(['contacts-wire-transfer', contact._id]);
    }
  });

  return {
    t,
    methods,
    modals,
    setModals,
    deleteModalState,
    dispatchDeleteModal,
    statesByCountry,
    kindList: kindList?.getListing.entries || [],
    existingMutations: existingMutations?.getListing.entries || [],
    existingQuerys: existingQuerys?.getListing.entries || [],
    existingRoles: existingRoles?.getRoles || [],
    // Mutations
    deleteAddress,
    deleteLicense,
    deleteEmail,
    deletePhone,
    deletePayment,
    deletePartyAssociation,
    updatePartyMutation,
    deleteUser,
    requestPasswordReset,
    // Loading states
    isUpdatingParty: responseUpdateParty.isLoading,
    isDeleteAddressLoading: responseDeleteAddress.isLoading,
    isRequestPasswordResetLoading,
    // Any delete in flight locks the shared confirm dialog (DESIGN.md §5).
    isDeleting: responseDeleteAddress.isLoading
      || responseDeletePhone.isLoading
      || responseDeleteEmail.isLoading
      || responseDeleteLicense.isLoading
      || isDeleteWireTransferLoading
      || responseDeletePartyAssociation.isLoading
      || responseDeleteUser.isLoading,
    isDeletePhoneLoading: responseDeletePhone.isLoading,
    isDeleteEmailLoading: responseDeleteEmail.isLoading,
    isDeleteLicenseLoading: responseDeleteLicense.isLoading,
    isDeleteWireTransferLoading,
    // Field arrays
    addresses,
    phones,
    emails,
    licenses,
    payments,
    parents,
    subordinates,
    permissions,
    getOptions,
    resetAICounters,
    isResettingAICounters,
    model_agent,
    isAdmin: currentUser?.is_admin || false,
    currentUserEmail: currentUser?.login_email
  };
};
